import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  InputBase,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";

import api from "../../services/api";

const STATUS_COLORS = {
  pending: "warning",
  open: "primary",
  closed: "default",
};

const ResultRow = ({ primary, secondary, meta, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2,
      py: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      cursor: "pointer",
      borderRadius: 1,
      mx: 0.5,
      "&:hover": { bgcolor: "action.hover" },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {primary}
      </Typography>
      {secondary && (
        <Typography variant="caption" color="text.disabled" noWrap display="block">
          {secondary}
        </Typography>
      )}
    </Box>
    {meta}
  </Box>
);

const SectionHeader = ({ icon: Icon, label }) => (
  <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: "flex", alignItems: "center", gap: 0.75 }}>
    <Icon sx={{ fontSize: 13, color: "text.disabled" }} />
    <Typography variant="overline" color="text.disabled" sx={{ lineHeight: 1, fontSize: "0.65rem" }}>
      {label}
    </Typography>
  </Box>
);

const LoadingSkeleton = () => (
  <Box sx={{ px: 2, py: 1 }}>
    {[1, 2, 3].map((i) => (
      <Skeleton key={i} variant="text" height={36} sx={{ borderRadius: 1, mb: 0.5 }} />
    ))}
  </Box>
);

const GlobalSearch = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [contacts, setContacts] = useState([]);

  const hasResults = tickets.length > 0 || contacts.length > 0;
  const showPanel = open && query.trim().length >= 2;

  useEffect(() => {
    if (query.trim().length < 2) {
      setTickets([]);
      setContacts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const [ticketRes, contactRes] = await Promise.all([
          api.get("/tickets", {
            params: { searchParam: query, showAll: "true", pageNumber: 1 },
          }),
          api.get("/contacts/", {
            params: { searchParam: query, pageNumber: 1 },
          }),
        ]);
        setTickets((ticketRes.data.tickets || []).slice(0, 5));
        setContacts((contactRes.data.contacts || []).slice(0, 5));
      } catch {
        // silently ignore search errors
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setQuery("");
      setOpen(false);
    }
  };

  const handleTicketClick = useCallback(
    (ticket) => {
      navigate(`/tickets/${ticket.id}`);
      setQuery("");
      setOpen(false);
    },
    [navigate]
  );

  const handleContactClick = useCallback(
    (contact) => {
      navigate(`/contacts`);
      setQuery("");
      setOpen(false);
    },
    [navigate]
  );

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: "100%" }}>
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          height: 34,
          borderRadius: 2,
          bgcolor: theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.08)
            : alpha(theme.palette.common.black, 0.05),
          border: "1px solid",
          borderColor: open
            ? theme.palette.primary.main
            : "transparent",
          transition: "border-color 0.15s, background-color 0.15s",
          "&:hover": {
            bgcolor: theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.12)
              : alpha(theme.palette.common.black, 0.07),
          },
        }}
      >
        {loading ? (
          <CircularProgress size={14} sx={{ color: "text.disabled", flexShrink: 0 }} />
        ) : (
          <SearchOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }} />
        )}
        <InputBase
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search tickets, contacts…"
          fullWidth
          sx={{
            fontSize: 13,
            "& input": {
              p: 0,
              color: "text.primary",
              "&::placeholder": { color: "text.disabled", opacity: 1 },
            },
          }}
        />
      </Box>

      {/* Results panel */}
      {showPanel && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.modal,
            borderRadius: 2,
            overflow: "hidden",
            maxHeight: 420,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {loading ? (
            <LoadingSkeleton />
          ) : !hasResults ? (
            <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No results for &ldquo;{query}&rdquo;
              </Typography>
            </Box>
          ) : (
            <>
              {tickets.length > 0 && (
                <>
                  <SectionHeader icon={SupportAgentOutlinedIcon} label="Tickets" />
                  {tickets.map((ticket) => (
                    <ResultRow
                      key={ticket.id}
                      primary={ticket.contact?.name || ticket.contact?.number || `Ticket #${ticket.ticketNumber || ticket.id}`}
                      secondary={ticket.lastMessage || ticket.contact?.number}
                      meta={
                        <Chip
                          label={ticket.status}
                          size="small"
                          color={STATUS_COLORS[ticket.status] || "default"}
                          sx={{ fontSize: "0.65rem", height: 20, flexShrink: 0 }}
                        />
                      }
                      onClick={() => handleTicketClick(ticket)}
                    />
                  ))}
                </>
              )}

              {tickets.length > 0 && contacts.length > 0 && (
                <Divider sx={{ my: 0.5 }} />
              )}

              {contacts.length > 0 && (
                <>
                  <SectionHeader icon={PersonOutlineOutlinedIcon} label="Contacts" />
                  {contacts.map((contact) => (
                    <ResultRow
                      key={contact.id}
                      primary={contact.name}
                      secondary={[contact.number, contact.email].filter(Boolean).join(" · ")}
                      onClick={() => handleContactClick(contact)}
                    />
                  ))}
                </>
              )}

              <Box sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="caption" color="text.disabled">
                  Press Esc to close
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSearch;
